<template>
  <div class="partners-view">
    <!-- 헤더 -->
    <div class="page-header">
      <div class="header-content">
        <h1 class="page-title">파트너스</h1>
        <div class="partner-count-badge">{{ partners.length }}</div>
      </div>
    </div>

    <!-- 로딩 -->
    <div v-if="loading" class="loading-container">
      <div class="partner-list">
        <div v-for="i in 8" :key="i" class="skeleton-item">
          <div class="skeleton-logo"></div>
          <div class="skeleton-content">
            <div class="skeleton-title"></div>
            <div class="skeleton-badge"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- 파트너 목록 -->
    <div v-else class="partner-list">
      <div 
        v-for="partner in partners" 
        :key="partner.id"
        class="partner-item"
        :class="{ expanded: expandedId === partner.id }"
        @click="toggleExpand(partner.id)"
      >
        <!-- 기본 정보 (항상 보임) -->
        <div class="partner-basic">
          <div class="partner-logo-area">
            <img :src="partner.logo" :alt="partner.name" class="partner-logo" />
          </div>
          
          <div class="partner-info">
            <h3 class="partner-name">{{ partner.name }}</h3>
            <span class="partner-category">{{ partner.category }}</span>
          </div>

          <div class="partner-actions">
            <a 
              v-if="partner.phone" 
              :href="`tel:${partner.phone}`" 
              class="quick-phone"
              @click.stop
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
              </svg>
            </a>
            
            <button class="expand-btn">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </button>
          </div>
        </div>

        <!-- 상세 정보 (펼쳤을 때만 보임) -->
        <transition name="slide-down">
          <div v-if="expandedId === partner.id" class="partner-details">
            <div class="details-grid">
              <!-- 소개 -->
              <div v-if="partner.description" class="detail-section full">
                <h4 class="detail-title">소개</h4>
                <p class="detail-text">{{ partner.description }}</p>
              </div>

              <!-- 연락처 -->
              <div class="detail-section">
                <h4 class="detail-title">연락처</h4>
                <div class="detail-list">
                  <div v-if="partner.phone" class="detail-item">
                    <svg class="detail-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                    </svg>
                    <a :href="`tel:${partner.phone}`" class="detail-link phone">
                      {{ partner.phone }}
                    </a>
                  </div>
                  <div v-if="partner.email" class="detail-item">
                    <svg class="detail-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                      <polyline points="22,6 12,13 2,6"/>
                    </svg>
                    <a :href="`mailto:${partner.email}`" class="detail-link email">
                      {{ partner.email }}
                    </a>
                  </div>
                  <div v-if="partner.website" class="detail-item">
                    <svg class="detail-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="2" y1="12" x2="22" y2="12"/>
                      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                    </svg>
                    <a :href="partner.website" target="_blank" rel="noopener noreferrer" class="detail-link website">
                      웹사이트 방문
                    </a>
                  </div>
                </div>
              </div>

              <!-- 주소 -->
              <div v-if="partner.address" class="detail-section">
                <h4 class="detail-title">주소</h4>
                <div class="detail-item">
                  <svg class="detail-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                    <circle cx="12" cy="10" r="3"/>
                  </svg>
                  <p class="detail-text">{{ partner.address }}</p>
                </div>
              </div>
            </div>
          </div>
        </transition>
      </div>

      <!-- 빈 상태 -->
      <div v-if="partners.length === 0" class="empty-state">
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
        </svg>
        <p>등록된 파트너가 없습니다</p>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { usePartners } from '@/composables/usePartners'

export default {
  name: 'PartnersView',
  setup() {
    const { getPartners, loading } = usePartners()
    const partners = ref([])
    const expandedId = ref(null)

    const toggleExpand = (partnerId) => {
      if (expandedId.value === partnerId) {
        expandedId.value = null
      } else {
        expandedId.value = partnerId
      }
    }

    onMounted(async () => {
      try {
        // 전체 파트너 조회 (제한 없음)
        partners.value = await getPartners(null)
      } catch (e) {
        console.error('파트너 로드 실패:', e)
      }
    })

    return {
      partners,
      loading,
      expandedId,
      toggleExpand
    }
  }
}
</script>

<style scoped>
.partners-view {
  min-height: 100vh;
  background: #f9fafb;
  padding-bottom: 80px;
}

/* 헤더 */
.page-header {
  background: #fff;
  border-bottom: 1px solid #f2f4f6;
  padding: 32px 24px;
}

.header-content {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 12px;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: #191f28;
  letter-spacing: -0.5px;
}

.partner-count-badge {
  padding: 6px 14px;
  background: #dbeafe;
  color: #3182f6;
  font-size: 15px;
  font-weight: 600;
  border-radius: 12px;
}

/* 로딩 */
.loading-container {
  max-width: 1200px;
  margin: 32px auto 0;
  padding: 0 24px;
}

.skeleton-item {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 12px;
}

.skeleton-logo {
  width: 120px;
  height: 60px;
  background: linear-gradient(90deg, #f2f4f6 25%, #e5e8eb 50%, #f2f4f6 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 8px;
  flex-shrink: 0;
}

.skeleton-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.skeleton-title {
  width: 200px;
  height: 20px;
  background: linear-gradient(90deg, #f2f4f6 25%, #e5e8eb 50%, #f2f4f6 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-badge {
  width: 80px;
  height: 16px;
  background: linear-gradient(90deg, #f2f4f6 25%, #e5e8eb 50%, #f2f4f6 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* 파트너 목록 */
.partner-list {
  max-width: 1200px;
  margin: 32px auto 0;
  padding: 0 24px;
}

.partner-item {
  background: #fff;
  border-radius: 12px;
  margin-bottom: 12px;
  border: 1px solid #e5e8eb;
  overflow: hidden;
  transition: all 0.2s;
  cursor: pointer;
}

.partner-item:hover {
  border-color: #d1d5db;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.partner-item.expanded {
  border-color: #3182f6;
  box-shadow: 0 4px 12px rgba(49, 130, 246, 0.08);
}

/* 기본 정보 */
.partner-basic {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 24px;
}

.partner-logo-area {
  width: 120px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.partner-logo {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.partner-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.partner-name {
  font-size: 18px;
  font-weight: 600;
  color: #191f28;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: -0.3px;
}

.partner-category {
  display: inline-block;
  padding: 4px 12px;
  background: #f2f4f6;
  color: #4e5968;
  font-size: 12px;
  font-weight: 500;
  border-radius: 6px;
  width: fit-content;
}

.partner-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.quick-phone {
  width: 40px;
  height: 40px;
  background: #f2f4f6;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #3182f6;
  transition: all 0.15s;
}

.quick-phone:hover {
  background: #3182f6;
  color: #fff;
}

.expand-btn {
  width: 40px;
  height: 40px;
  background: transparent;
  border: 1px solid #e5e8eb;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7684;
  cursor: pointer;
  transition: all 0.2s;
}

.partner-item.expanded .expand-btn {
  background: #3182f6;
  border-color: #3182f6;
  color: #fff;
  transform: rotate(180deg);
}

.expand-btn:hover {
  border-color: #d1d5db;
  background: #f9fafb;
}

.partner-item.expanded .expand-btn:hover {
  background: #2563eb;
  border-color: #2563eb;
}

/* 상세 정보 */
.partner-details {
  padding: 0 24px 24px 24px;
  border-top: 1px solid #e5e8eb;
  background: #f9fafb;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  padding-top: 20px;
}

.detail-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.detail-section.full {
  grid-column: 1 / -1;
  padding-bottom: 4px;
  border-bottom: 1px solid #e5e8eb;
}

.detail-title {
  font-size: 13px;
  font-weight: 600;
  color: #6b7684;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-text {
  font-size: 14px;
  color: #4e5968;
  line-height: 1.6;
  margin: 0;
}

.detail-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.detail-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.detail-icon {
  color: #6b7684;
  flex-shrink: 0;
}

.detail-link {
  font-size: 14px;
  color: #191f28;
  transition: color 0.15s;
  font-weight: 500;
}

.detail-link:hover {
  color: #3182f6;
}

.detail-link.phone {
  color: #3182f6;
}

/* 슬라이드 애니메이션 */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.25s ease-out;
  max-height: 500px;
  opacity: 1;
}

.slide-down-enter-from,
.slide-down-leave-to {
  max-height: 0;
  opacity: 0;
  overflow: hidden;
}

/* 빈 상태 */
.empty-state {
  text-align: center;
  padding: 80px 20px;
  color: #868e96;
}

.empty-state svg {
  margin-bottom: 16px;
  opacity: 0.3;
  color: #adb5bd;
}

.empty-state p {
  font-size: 15px;
  color: #868e96;
}

/* 반응형 */
@media (max-width: 768px) {
  .page-header {
    padding: 24px 16px;
  }

  .page-title {
    font-size: 24px;
  }

  .partner-count-badge {
    font-size: 14px;
    padding: 5px 12px;
  }

  .loading-container,
  .partner-list {
    padding: 0 16px;
    margin-top: 24px;
  }

  .partner-basic {
    padding: 16px;
    gap: 12px;
  }

  .partner-logo-area {
    width: 80px;
    height: 40px;
  }

  .partner-name {
    font-size: 16px;
  }

  .partner-details {
    padding: 0 16px 16px 16px;
  }

  .details-grid {
    grid-template-columns: 1fr;
    gap: 20px;
    padding-top: 16px;
  }

  .quick-phone,
  .expand-btn {
    width: 36px;
    height: 36px;
  }

  .quick-phone svg {
    width: 14px;
    height: 14px;
  }

  .expand-btn svg {
    width: 18px;
    height: 18px;
  }
}
</style>